import React from "react";
import { Camera, ChevronDown, ChevronLeft, ChevronRight, Loader2, LogOut, Mail, Phone } from "lucide-react";

// ===== GET USER INITIALS =====
// Empty when there is no confirmed name — the avatar then renders as a
// neutral skeleton instead of invented initials.
export const getUserInitials = (name) => {
  if (!name || !name.trim()) return "";
  const words = name.trim().split(" ");
  if (words.length === 1) return words[0].charAt(0).toUpperCase();
  return (
    words[0].charAt(0) + words[words.length - 1].charAt(0)
  ).toUpperCase();
};

// Placeholder while the identity isn't known yet (e.g. mid sign-out).
// <span>s so they stay valid inside the header <button>.
const SKELETON_BG = "rgba(148, 163, 184, 0.25)";

const NameSkeleton = ({ width }) => (
  <span
    aria-hidden="true"
    style={{ display: "inline-block", width, height: "0.75rem", borderRadius: 6, background: SKELETON_BG }}
  />
);

// Uploaded photo, or brand-blue initials when there isn't one, or a neutral
// placeholder when there is no confirmed name at all.
const Avatar = ({ src, name, style, fontSize }) =>
  src ? (
    <img src={src} alt="" style={style} />
  ) : !getUserInitials(name) ? (
    <span aria-hidden="true" style={{ ...style, display: "block", background: SKELETON_BG }} />
  ) : (
    <span
      style={{
        ...style,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#2e7cba",
        color: "#ffffff",
        fontSize,
        fontWeight: "700",
        textTransform: "uppercase",
      }}
    >
      {getUserInitials(name)}
    </span>
  );

const InfoRow = ({ icon: Icon, label, value, emptyText, styles }) => (
  <div style={styles.userProfileItem}>
    <span style={styles.userProfileItemIcon}>
      <Icon size={16} />
    </span>
    <span style={styles.userProfileItemText}>
      <span style={styles.userProfileItemLabel}>{label}</span>
      <span
        style={
          value
            ? styles.userProfileItemValue
            : { ...styles.userProfileItemValue, ...styles.userProfileItemValueEmpty }
        }
        title={value || undefined}
      >
        {value || emptyText}
      </span>
    </span>
  </div>
);

// ===== USER PROFILE (topbar trigger + dropdown) =====
// This block was duplicated verbatim in the RTL and LTR branches of every
// sidebar copy — now it's a single component rendered once.
//
// `onOpenProfile` — opens the viewer's own profile (switching to User View).
// Omitted for accounts with no profile page (superadmin), in which case the
// header is a plain, non-clickable summary.
//
// `onChangePhoto` (+ `photoInputRef`, `onPhotoSelected`, `isUploadingPhoto`)
// — camera badge on the header photo, same upload as the profile page.
// Omitted for accounts without an employee record (no picture to change).
const UserProfileDropdown = ({
  styles,
  t,
  language,
  isMobile,
  userData,
  profilePic,
  showUserProfile,
  toggleUserProfile,
  userProfileRef,
  getStatusColor,
  onOpenProfile,
  onLogoutClick,
  onChangePhoto,
  isUploadingPhoto = false,
  photoInputRef,
  onPhotoSelected,
}) => {
  const isAr = language === "ar";
  const displayName = userData.name || "";
  const avatarSrc = profilePic ? userData.avatar : null;
  const statusColor = getStatusColor(userData.status);
  const viewProfileLabel = t.viewMyProfile || "View my profile";
  const ArrowIcon = isAr ? ChevronLeft : ChevronRight;
  const photoLabel = isUploadingPhoto ? t.uploadingPhoto || "Uploading photo…" : t.changePhoto || "Change photo";
  // Camera badge on the header photo — a small corner badge on phones, where
  // the full-size one covered a third of the avatar.
  const photoBtnSize = isMobile ? 1.25 : 1.75; // rem
  const photoIconSize = isMobile ? 10 : 13;

  const handleTriggerKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      toggleUserProfile(e);
    }
  };

  const headerContent = (
    <>
      <Avatar
        src={avatarSrc}
        name={userData.name}
        style={styles.userProfileAvatar}
        fontSize="1.25rem"
      />
      <span style={styles.userProfileIdentity}>
        <span style={styles.userProfileName} title={displayName || undefined}>
          {displayName || <NameSkeleton width="8rem" />}
        </span>
        <span style={styles.userProfileStatus}>
          <span style={{ ...styles.userProfileStatusDot, background: statusColor }} />
          <span style={styles.userProfileStatusText}>
            {userData.status === "online" ? t.activeNow : t.connectionOffline}
          </span>
        </span>
      </span>
      {onOpenProfile && (
        <ArrowIcon
          size={18}
          style={styles.userProfileHeaderArrow}
          className="user-menu-header-arrow"
        />
      )}
    </>
  );

  return (
    <div ref={userProfileRef} style={{ position: "relative" }}>
      {/* ── Topbar chip ── */}
      <div
        style={styles.userInfo}
        className="user-info"
        onClick={toggleUserProfile}
        onKeyDown={handleTriggerKeyDown}
        role="button"
        tabIndex={0}
        aria-haspopup="true"
        aria-expanded={showUserProfile}
        title={displayName || undefined}
      >
        <div style={{ position: "relative", flexShrink: 0 }}>
          <Avatar
            src={avatarSrc}
            name={userData.name}
            style={styles.userAvatar}
            fontSize={isMobile ? "0.8125rem" : "0.875rem"}
          />
          <span style={{ ...styles.userAvatarStatus, background: statusColor }} />
        </div>
        <div style={styles.userDetails} className="user-details">
          <span style={styles.userName}>
            {userData.name ? userData.name.split(/\s+/)[0] : <NameSkeleton width="3.5rem" />}
          </span>
        </div>
        <ChevronDown
          size={14}
          style={styles.userDropdownIcon}
          className="user-dropdown-icon"
        />
      </div>

      {/* ── Dropdown ── */}
      <div
        style={styles.userProfileDropdown}
        className="user-profile-dropdown"
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ position: "relative" }}>
          {onOpenProfile ? (
            <button
              type="button"
              className="user-menu-header"
              style={{ ...styles.userProfileHeader, ...styles.userProfileHeaderButton }}
              onClick={onOpenProfile}
              title={viewProfileLabel}
            >
              {headerContent}
            </button>
          ) : (
            <div style={styles.userProfileHeader}>{headerContent}</div>
          )}

          {/* Sits over the photo's bottom corner, outside the header button
              (a button can't contain another button). */}
          {onChangePhoto && (
            <>
              <button
                type="button"
                className="user-menu-photo-btn"
                onClick={onChangePhoto}
                disabled={isUploadingPhoto}
                aria-label={photoLabel}
                title={photoLabel}
                style={{
                  position: "absolute",
                  // Bottom corner of the 3.5rem avatar, overlapping it by ~3/4 of the badge.
                  top: `calc(50% + 1.75rem - ${photoBtnSize * 0.85}rem)`,
                  insetInlineStart: `calc(0.875rem + 3.5rem - ${photoBtnSize * 0.85}rem)`,
                  width: `${photoBtnSize}rem`,
                  height: `${photoBtnSize}rem`,
                  padding: 0,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "#2e7cba",
                  color: "#ffffff",
                  border: `${isMobile ? 1.5 : 2}px solid #ffffff`,
                  boxShadow: "0 2px 6px rgba(15, 23, 42, 0.25)",
                  cursor: isUploadingPhoto ? "wait" : "pointer",
                  opacity: isUploadingPhoto ? 0.75 : 1,
                  zIndex: 1,
                }}
              >
                {isUploadingPhoto ? (
                  <Loader2 size={photoIconSize} style={{ animation: "spin 1s linear infinite" }} />
                ) : (
                  <Camera size={photoIconSize} />
                )}
              </button>
              <input
                ref={photoInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={(e) => {
                  onPhotoSelected?.(e);
                  // Same file picked twice in a row should upload again.
                  e.target.value = "";
                }}
              />
            </>
          )}
        </div>

        <div style={styles.userProfileContent}>
          <div style={styles.userProfileSection}>
            <div style={styles.userProfileSectionTitle}>
              {t.contact || "Contact"}
            </div>
            <InfoRow
              icon={Mail}
              label={t.emailLabel || "Email"}
              value={userData.email}
              emptyText={t.noEmail || "No email"}
              styles={styles}
            />
            <InfoRow
              icon={Phone}
              label={t.phoneLabel || "Phone"}
              value={userData.phone}
              emptyText={t.noPhone || "No phone"}
              styles={styles}
            />
          </div>

          <div style={styles.userProfileDivider} />

          <button
            type="button"
            className="user-menu-logout"
            style={styles.logoutBtn}
            onClick={onLogoutClick}
          >
            <span style={styles.logoutBtnIcon}>
              <LogOut size={16} style={{ transform: isAr ? "scaleX(-1)" : "none" }} />
            </span>
            <span>{t.logout || "Logout"}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserProfileDropdown;
